import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
    AttemptedCard,
    UnattemptedCard,
    LockedCard,
} from "components/Dashboard/TestCard";
import { getMockTemplates } from "services/sectionService";
import {
    getIELTSPlan,
    getFullTestState,
    findAttemptForTemplate,
    hasAllFourSections,
} from "utils/ieltsAccess";

function formatDuration(seconds) {
    if (!seconds) return "";
    const mins = Math.round(seconds / 60);
    return `~${mins} minutes`;
}

function buildCard(template, index, plan, mockAttempts) {
    const sectionCount = template.sections?.length || 0;
    const duration = formatDuration(template.duration);
    const metaParts = [];
    if (sectionCount) metaParts.push(`${sectionCount} sections`);
    if (duration) metaParts.push(duration);

    const templateId = String(template._id || template.templateId || '');

    const matchedAttempt = findAttemptForTemplate(template, mockAttempts);

    const state = matchedAttempt ? "attempted" : getFullTestState(templateId, plan);

    return {
        id: templateId,
        title: `Test ${index + 1}`,
        meta: metaParts.join(" • "),
        state,
        band: matchedAttempt ? (matchedAttempt.summary?.overall?.score ?? null) : undefined,
        attemptId: matchedAttempt?._id,
        sections: template.sections,
    };
}

function renderCard(card, onAction) {
    const commonProps = { icon: AssignmentOutlinedIcon, title: card.title, meta: card.meta };

    switch (card.state) {
        case "attempted":
            return (
                <AttemptedCard
                    key={card.id} {...commonProps}
                    band={card.band}
                    onViewResults={() => onAction("view_results", card)}
                />
            );
        case "locked":
            return (
                <LockedCard
                    key={card.id} {...commonProps}
                    onUnlock={() => onAction("unlock", card)}
                />
            );
        case "free_prompt":
            return (
                <UnattemptedCard
                    key={card.id} {...commonProps}
                    onStartTest={() => onAction("free_prompt", card)}
                />
            );
        case "unattempted":
        default:
            return (
                <UnattemptedCard
                    key={card.id} {...commonProps}
                    onStartTest={() => onAction("start_test", card)}
                />
            );
    }
}

const CARD_WIDTH = 380;
const CARD_GAP = 24;
const SCROLL_BY = CARD_WIDTH + CARD_GAP;

export default function PracticeByTests({ hasAttempts = false, mockAttempts = [], onAction }) {
    const [rawTemplates, setRawTemplates] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const scrollRef = React.useRef(null);
    const dragRef = React.useRef({ isDragging: false, startX: 0, scrollLeft: 0 });

    const scroll = (dir) => {
        scrollRef.current?.scrollBy({ left: dir * SCROLL_BY, behavior: "smooth" });
    };

    const onMouseDown = (e) => {
        const el = scrollRef.current;
        dragRef.current = { isDragging: true, startX: e.pageX, scrollLeft: el.scrollLeft };
        el.style.cursor = "grabbing";
        el.style.userSelect = "none";
        el.style.scrollSnapType = "none";
    };

    const onMouseMove = (e) => {
        if (!dragRef.current.isDragging) return;
        const dx = e.pageX - dragRef.current.startX;
        scrollRef.current.scrollLeft = dragRef.current.scrollLeft - dx;
    };

    const onMouseUp = () => {
        const el = scrollRef.current;
        dragRef.current.isDragging = false;
        el.style.cursor = "grab";
        el.style.userSelect = "";
        el.style.scrollSnapType = "";
    };

    // Fetch templates once — only stores raw data, no plan logic here
    React.useEffect(() => {
        let isMounted = true;

        (async () => {
            try {
                const data = await getMockTemplates({
                    mockType: "ielts",
                    pageSize: 30,
                    pageNumber: 1,
                });

                const templates = Array.isArray(data) ? data : data?.templates || [];
                const fullTests = templates.filter(hasAllFourSections);

                console.log("[PracticeByTests] raw plan from session:", getIELTSPlan());
                console.log("[PracticeByTests] full-test templates found:", fullTests.length);

                if (isMounted) {
                    setRawTemplates(fullTests);
                }
            } catch (error) {
                console.error("Failed to fetch full test templates:", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        })();

        return () => { isMounted = false; };
    }, []);

    // Re-compute card states every time hasAttempts, mockAttempts, or the template list changes
    const { cards, planType } = React.useMemo(() => {
        const plan = { ...getIELTSPlan(), hasAttempts };
        return {
            planType: plan.planType,
            cards: rawTemplates.map((t, i) => buildCard(t, i, plan, mockAttempts)),
        };
    }, [rawTemplates, hasAttempts, mockAttempts]);


    const handleAction = onAction || ((action, card) => console.log(action, card));

    if (!loading && cards.length === 0) return null;

    return (
        <Box
            id="practice-by-tests"
            sx={{
                backgroundColor: "#F8FAFC",
                width: "100%",
                px: "76px",
                py: "64px",
            }}
        >
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: "48px" }}>
                <Typography
                    sx={{
                        color: "#111827",
                        fontFamily: "Mulish, sans-serif",
                        fontWeight: 800,
                        fontSize: { xs: "1.75rem", md: "2.25rem" },
                        mb: "12px",
                    }}
                >
                    Practice by Tests
                </Typography>
                <Typography
                    sx={{
                        color: "#6B7280",
                        fontFamily: "Mulish, sans-serif",
                        fontSize: "0.975rem",
                        lineHeight: 1.6,
                    }}
                >
                    Full-length IELTS mock tests covering all four sections — Listening, Reading, Writing &amp; Speaking.
                </Typography>
            </Box>

            {/* No-plan banner */}
            {!loading && planType === null && (
                <Box
                    sx={{
                        backgroundColor: "#FFF7ED",
                        border: "1px solid #FDBA74",
                        borderRadius: "10px",
                        px: "28px",
                        py: "20px",
                        mb: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "16px",
                        flexWrap: "wrap",
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "Mulish, sans-serif",
                                fontWeight: 700,
                                fontSize: "1rem",
                                color: "#92400E",
                                mb: "4px",
                            }}
                        >
                            Unlock Full Access
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "Mulish, sans-serif",
                                fontSize: "0.875rem",
                                color: "#B45309",
                            }}
                        >
                            You don&apos;t have an active plan. Purchase a plan to start practising.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        component="a"
                        href="/"
                        disableElevation
                        sx={{
                            backgroundColor: "#F59E0B",
                            color: "#FFFFFF",
                            borderRadius: "6px",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            fontFamily: "Mulish, sans-serif",
                            whiteSpace: "nowrap",
                            "&:hover": { backgroundColor: "#D97706" },
                        }}
                    >
                        View Pricing
                    </Button>
                </Box>
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: "48px" }}>
                    <CircularProgress size={32} />
                </Box>
            ) : (
                <Box sx={{ position: "relative" }}>
                    {/* Left arrow */}
                    <IconButton
                        onClick={() => scroll(-1)}
                        sx={{
                            position: "absolute", left: -44, top: "50%",
                            transform: "translateY(-50%)", zIndex: 1,
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #E5E7EB",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                            "&:hover": { backgroundColor: "#F9FAFB" },
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>

                    {/* Scrollable row */}
                    <Box
                        ref={scrollRef}
                        onMouseDown={onMouseDown}
                        onMouseMove={onMouseMove}
                        onMouseUp={onMouseUp}
                        onMouseLeave={onMouseUp}
                        sx={{
                            display: "flex",
                            gap: `${CARD_GAP}px`,
                            overflowX: "auto",
                            scrollSnapType: "x mandatory",
                            pb: "8px",
                            cursor: "grab",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                        }}
                    >
                        {cards.map((card) => (
                            <Box
                                key={card.id}
                                sx={{ width: CARD_WIDTH, minWidth: CARD_WIDTH, maxWidth: CARD_WIDTH, flexShrink: 0, flexGrow: 0, scrollSnapAlign: "start", display: "flex", p: "4px" }}
                            >
                                {renderCard(card, handleAction)}
                            </Box>
                        ))}
                    </Box>

                    {/* Right arrow */}
                    <IconButton
                        onClick={() => scroll(1)}
                        sx={{
                            position: "absolute", right: -44, top: "50%",
                            transform: "translateY(-50%)", zIndex: 1,
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #E5E7EB",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                            "&:hover": { backgroundColor: "#F9FAFB" },
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}
